<template>
  <SimpleFormModal
    class="custom-modal"
    :title="title"
    :submit-button-text="submitButtonText"
    :open="isOpen"
    :disable-submit="submitDisabled"
    @close="handleClose"
    @submit="handleSubmit"
  >
    <template #content>
      <div class="container">
        <DirectoryListView
          :directoryItems="fileManager.displayedItems.value"
          :actions="[]"
          :disabled-types="['file', 'link']"
          :multi-select-enabled="multiSelect"
          @select="handleSelect"
          @doubleClick="fileManager.openItem"
          @load-more-items="fileManager.loadMoreItems"
        />
      </div>
    </template>
    <template #footer>
      <div class="footer">
        <BreadcrumbFiles
          :breadcrumbs="fileManager.breadcrumbs.value"
          @onClicked="onBreadcrumbClick"
        />
      </div>
    </template>
  </SimpleFormModal>
</template>
<script setup lang="ts">
import DirectoryListView from './list-view/DirectoryListView.vue'
import SimpleFormModal from './SimpleFormModal.vue'
import { computed, ref, toRefs, watch } from 'vue'
import { DirectoryItem } from './types'
import BreadcrumbFiles from './BreadcrumbFiles.vue'
import { useFileManagerConnected } from './useFileManagerConnected'

type Props = {
  title: string
  path: DirectoryItem<'directory'>[]
  submitButtonText?: string
  multiSelect?: boolean
  open?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  submitButtonText: 'Submit',
  multiSelect: false,
  open: false
})

const { title, submitButtonText, path } = toRefs(props)
const fileManager = useFileManagerConnected(path.value[path.value.length - 1]?.id)

const selectedItems = ref<DirectoryItem[]>([])

const emit = defineEmits<{
  (e: 'submit', items: DirectoryItem[]): void
  (e: 'close'): void
}>()

const isOpen = ref(props.open)

const submitDisabled = computed(() => {
  return false
})

watch(
  path,
  async (newPath) => {
    if (newPath.length) {
      const current = newPath[newPath.length - 1]
      await fileManager.addPath(newPath)
      await fileManager.openItem(current)
      await fileManager.refresh()
    }
  },
  { immediate: true }
)

watch(
  () => props.open,
  (newValue) => {
    isOpen.value = newValue
  },
  { immediate: true }
)

const handleSelect = (items: DirectoryItem[]) => {
  selectedItems.value = items
}

const handleSubmit = () => {
  if (!submitDisabled.value) {
    isOpen.value = false
    if (selectedItems.value.length === 0) {
      const node = fileManager.currentDirectory.value
      if (node) {
        emit('submit', [node])
      }
    } else {
      emit('submit', selectedItems.value)
    }
  }
  selectedItems.value = []
}

const handleClose = () => {
  selectedItems.value = []
  isOpen.value = false
  emit('close')
}

async function onBreadcrumbClick(option: DirectoryItem) {
  if (!option.id) {
    console.warn('Breadcrumb has no id—skipping navigation', option)
    return
  }
  await fileManager.openItem(option.id)
}
</script>

<style lang="scss" scoped>
.container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 500px;
  height: 400px;
  width: 500px;
}
.footer {
  max-width: 500px;
}
</style>
